import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Check, TrendingUp, Users, Megaphone, Award, Send, Sparkles, Building2, Eye, Target, BarChart3, Lightbulb, Palette, MapPin, Store, Camera, Globe, Heart } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useTheme } from '../context/ThemeContext';
import { useToast } from '../hooks/use-toast';
import { useTranslation } from 'react-i18next';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const BusinessEnhancement = () => {
  const { isDark } = useTheme();
  const { toast } = useToast();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    businessType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const quoteData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        sign_type: `Brand Boost - ${formData.businessType || 'General Inquiry'}`,
        message: `Business: ${formData.company}\nType: ${formData.businessType}\n\nChallenges/Goals:\n${formData.message}`,
        source: 'Brand Boost Consultation'
      };

      const response = await fetch(`${API_URL}/api/quotes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(quoteData)
      });

      if (response.ok) {
        toast({
          title: t('brandBoost.submitSuccess'),
          description: t('brandBoost.submitSuccessDesc'),
        });
        setFormData({ name: '', email: '', phone: '', company: '', businessType: '', message: '' });
      } else {
        toast({
          title: 'Submission Failed',
          description: 'Please try again or contact us directly.',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        title: 'Error',
        description: 'Failed to submit. Please try again.',
        variant: 'destructive'
      });
    }
    
    setIsSubmitting(false);
  };

  const mainServices = [
    {
      icon: Eye,
      title: t('brandBoost.brandAssessment'),
      description: t('brandBoost.brandAssessmentDesc'),
      features: [t('brandBoost.bFeature1'), t('brandBoost.bFeature2'), t('brandBoost.bFeature3'), t('brandBoost.bFeature4')],
      color: isDark ? '#ff00ff' : '#ff1493'
    },
    {
      icon: Megaphone,
      title: t('brandBoost.advertising'),
      description: t('brandBoost.advertisingDesc'),
      features: [t('brandBoost.bFeature5'), t('brandBoost.bFeature6'), t('brandBoost.bFeature7'), t('brandBoost.bFeature8')],
      color: isDark ? '#00ffff' : '#00bfff'
    },
    {
      icon: Store,
      title: t('brandBoost.storefront'),
      description: t('brandBoost.storefrontDesc'),
      features: [t('brandBoost.bFeature9'), t('brandBoost.bFeature10'), t('brandBoost.bFeature11'), t('brandBoost.bFeature12')],
      color: isDark ? '#ffff00' : '#f59e0b'
    },
    {
      icon: TrendingUp,
      title: t('brandBoost.growth'),
      description: t('brandBoost.growthDesc'),
      features: [t('brandBoost.bFeature13'), t('brandBoost.bFeature14'), t('brandBoost.bFeature15'), t('brandBoost.bFeature16')],
      color: isDark ? '#00ff88' : '#10b981'
    }
  ];

  const advertisingServices = [
    {
      icon: Building2,
      title: t('brandBoost.outdoorAdvertising'),
      description: t('brandBoost.outdoorAdvertisingDesc')
    },
    {
      icon: MapPin,
      title: t('brandBoost.locationMarketing'),
      description: t('brandBoost.locationMarketingDesc')
    },
    {
      icon: Camera,
      title: t('brandBoost.visualContent'),
      description: t('brandBoost.visualContentDesc')
    },
    {
      icon: Globe,
      title: t('brandBoost.onlineOffline'),
      description: t('brandBoost.onlineOfflineDesc')
    }
  ];

  const brandImageServices = [
    {
      title: t('brandBoost.visualIdentityAudit'),
      items: [t('quoteForm.name'), t('brandBoost.bFeature2'), t('brandBoost.bFeature3'), t('brandBoost.bFeature4')]
    },
    {
      title: t('brandBoost.customerPerception'),
      items: [t('brandBoost.bFeature1'), t('brandBoost.bFeature2'), t('brandBoost.bFeature3'), t('brandBoost.bFeature4')]
    },
    {
      title: t('brandBoost.physicalPresence'),
      items: [t('brandBoost.bFeature9'), t('brandBoost.bFeature10'), t('brandBoost.bFeature11'), t('brandBoost.bFeature12')]
    },
    {
      title: t('brandBoost.marketingMaterial'),
      items: [t('brandBoost.bFeature5'), t('brandBoost.bFeature6'), t('brandBoost.bFeature7'), t('brandBoost.bFeature8')]
    }
  ];

  const benefits = [
    {
      icon: Users,
      title: t('brandBoost.attractCustomers'),
      description: t('brandBoost.attractCustomersDesc'),
      stat: '40%',
      statLabel: t('brandBoost.attractCustomersDesc')
    },
    {
      icon: Award,
      title: t('brandBoost.buildTrust'),
      description: t('brandBoost.buildTrustDesc'),
      stat: '68%',
      statLabel: t('brandBoost.buildTrustDesc')
    },
    {
      icon: Heart,
      title: t('brandBoost.customerLoyalty'),
      description: t('brandBoost.customerLoyaltyDesc'),
      stat: '3x',
      statLabel: t('brandBoost.customerLoyaltyDesc')
    },
    {
      icon: TrendingUp,
      title: t('brandBoost.increaseRevenue'),
      description: t('brandBoost.increaseRevenueDesc'),
      stat: '25%',
      statLabel: t('brandBoost.increaseRevenueDesc')
    }
  ];

  const processSteps = [
    { 
      step: '01', 
      title: t('brandBoost.discoverySession'), 
      description: t('brandBoost.discoverySessionDesc'),
    },
    { 
      step: '02', 
      title: t('brandBoost.comprehensiveAudit'), 
      description: t('brandBoost.comprehensiveAuditDesc'),
    },
    { 
      step: '03', 
      title: t('brandBoost.strategyDevelopment'), 
      description: t('brandBoost.strategyDevelopmentDesc'),
    },
    { 
      step: '04', 
      title: t('brandBoost.implementationSupport'), 
      description: t('brandBoost.implementationSupportDesc'),
    }
  ];

  const whoWeHelp = [
    'Retail stores & boutiques',
    'Restaurants & cafes',
    'Professional services (law, medical, financial)',
    'Salons & spas',
    'Gyms & fitness studios',
    'Auto dealerships & service centers',
    'Hotels & hospitality',
    'Real estate offices',
    'Educational institutions',
    'Healthcare facilities',
    'Entertainment venues',
    'Franchise businesses'
  ];

  return (
    <div 
      className="min-h-screen pt-24 md:pt-28 pb-16 transition-colors duration-300"
      style={{ backgroundColor: isDark ? '#000000' : '#ffffff' }}
    >
      <div className="max-w-7xl mx-auto px-4">
        {/* Back Button */}
        <Link 
          to="/"
          className="inline-flex items-center gap-2 mb-8 font-medium transition-all duration-300 hover:scale-105"
          style={{
            color: isDark ? '#00ffff' : '#0f172a',
            textShadow: isDark ? '0 0 10px rgba(0, 255, 255, 0.5)' : 'none'
          }}
          data-testid="back-to-home"
        >
          <ArrowLeft className="w-5 h-5" />
          {t('common.backToHome')}
        </Link>

        {/* Hero Section */}
        <div className="text-center mb-16">
          <div className="flex justify-center mb-6">
            <div 
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border"
              style={{
                backgroundColor: isDark ? 'rgba(20, 20, 20, 0.8)' : 'rgba(255, 255, 255, 0.9)',
                borderColor: isDark ? '#00ffff' : '#00bfff',
                borderWidth: '2px'
              }}
            >
              <Sparkles className="w-4 h-4" style={{ color: isDark ? '#00ffff' : '#00bfff' }} />
              <span style={{ color: isDark ? '#ff00ff' : '#ff1493' }}>{t('brandBoost.badge')}</span>
            </div>
          </div>
          
          <h1 
            className={`text-4xl md:text-6xl font-bold mb-6 ${isDark ? 'gradient-text-dark' : 'gradient-text-light'}`}
            style={{
              filter: isDark 
                ? 'drop-shadow(0 0 20px rgba(255, 0, 255, 0.5))'
                : 'drop-shadow(0 0 15px rgba(255, 20, 147, 0.4))'
            }}
          >
            {t('brandBoost.title')}
          </h1>
          <p 
            className="text-lg md:text-xl max-w-4xl mx-auto mb-4"
            style={{ color: isDark ? '#e0e0e0' : '#334155' }}
          >
            {t('brandBoost.subtitle1')}
          </p>
          <p 
            className="text-base md:text-lg max-w-3xl mx-auto"
            style={{ color: isDark ? '#a0a0a0' : '#64748b' }}
          >
            {t('brandBoost.subtitle2')}
          </p>
        </div>

        {/* Benefits with Stats */}
        <div className="mb-20">
          <h2 
            className="text-3xl md:text-4xl font-bold text-center mb-4"
            style={{
              color: isDark ? '#ff00ff' : '#ff1493',
              textShadow: isDark ? '0 0 20px rgba(255, 0, 255, 0.6)' : 'none'
            }}
          >
            {t('brandBoost.whyInvest')}
          </h2>
          <p 
            className="text-center max-w-2xl mx-auto mb-12"
            style={{ color: isDark ? '#a0a0a0' : '#64748b' }}
          >
            {t('brandBoost.whyInvestSubtitle')}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <div 
                key={index}
                className="p-6 rounded-2xl text-center transition-all duration-300 hover:scale-105"
                style={{
                  backgroundColor: isDark ? 'rgba(20, 20, 20, 0.8)' : 'rgba(248, 250, 252, 1)',
                  border: `2px solid ${isDark ? 'rgba(255, 0, 255, 0.3)' : 'rgba(255, 20, 147, 0.2)'}`,
                  boxShadow: isDark ? '0 0 30px rgba(255, 0, 255, 0.1)' : 'none'
                }}
              >
                <div 
                  className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4"
                  style={{
                    background: isDark 
                      ? 'linear-gradient(135deg, rgba(255, 0, 255, 0.2) 0%, rgba(0, 255, 255, 0.2) 100%)'
                      : 'linear-gradient(135deg, rgba(255, 20, 147, 0.15) 0%, rgba(0, 191, 255, 0.15) 100%)'
                  }}
                >
                  <benefit.icon className="w-7 h-7" style={{ color: isDark ? '#00ffff' : '#0ea5e9' }} />
                </div>
                <div 
                  className="text-4xl font-bold mb-1"
                  style={{ 
                    color: isDark ? '#ffff00' : '#f59e0b',
                    textShadow: isDark ? '0 0 15px rgba(255, 255, 0, 0.6)' : 'none'
                  }}
                >
                  {benefit.stat}
                </div>
                <div 
                  className="text-xs mb-3"
                  style={{ color: isDark ? '#808080' : '#94a3b8' }}
                >
                  {benefit.statLabel}
                </div>
                <h3 
                  className="text-lg font-bold mb-2"
                  style={{ color: isDark ? '#ffffff' : '#0f172a' }}
                >
                  {benefit.title}
                </h3>
                <p 
                  className="text-sm"
                  style={{ color: isDark ? '#a0a0a0' : '#64748b' }}
                >
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Main Services */}
        <div className="mb-20">
          <h2 
            className="text-3xl md:text-4xl font-bold text-center mb-4"
            style={{
              color: isDark ? '#00ffff' : '#0ea5e9',
              textShadow: isDark ? '0 0 20px rgba(0, 255, 255, 0.6)' : 'none'
            }}
          >
            {t('brandBoost.ourServices')}
          </h2>
          <p 
            className="text-center max-w-2xl mx-auto mb-12"
            style={{ color: isDark ? '#a0a0a0' : '#64748b' }}
          >
            {t('brandBoost.ourServicesSubtitle')}
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {mainServices.map((service, index) => (
              <div
                key={index}
                className="p-8 rounded-2xl border transition-all duration-300 hover:scale-[1.01]"
                style={{
                  backgroundColor: isDark ? 'rgba(20, 20, 20, 0.8)' : 'rgba(255, 255, 255, 0.9)',
                  borderColor: service.color,
                  borderWidth: '2px',
                  boxShadow: isDark ? `0 0 40px ${service.color}22` : `0 0 30px ${service.color}15`
                }}
              >
                <div 
                  className="w-16 h-16 rounded-xl flex items-center justify-center mb-5"
                  style={{
                    background: `${service.color}22`,
                    boxShadow: isDark ? `0 0 30px ${service.color}44` : 'none'
                  }}
                >
                  <service.icon className="w-8 h-8" style={{ color: service.color }} />
                </div>
                <h3 
                  className="text-2xl font-bold mb-3"
                  style={{ 
                    color: service.color,
                    textShadow: isDark ? `0 0 15px ${service.color}88` : 'none'
                  }}
                >
                  {service.title}
                </h3>
                <p 
                  className="mb-5"
                  style={{ color: isDark ? '#c0c0c0' : '#64748b' }}
                >
                  {service.description}
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {service.features.map((feature, fIndex) => (
                    <div 
                      key={fIndex}
                      className="flex items-center gap-2 text-sm"
                      style={{ color: isDark ? '#a0a0a0' : '#64748b' }}
                    >
                      <Check className="w-4 h-4 flex-shrink-0" style={{ color: isDark ? '#00ff88' : '#10b981' }} />
                      {feature}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Advertising Services */}
        <div 
          className="p-8 md:p-12 rounded-2xl mb-20"
          style={{
            background: isDark 
              ? 'linear-gradient(135deg, rgba(255, 0, 255, 0.08) 0%, rgba(0, 255, 255, 0.08) 100%)'
              : 'linear-gradient(135deg, rgba(255, 20, 147, 0.05) 0%, rgba(0, 191, 255, 0.05) 100%)',
            border: `2px solid ${isDark ? '#ff00ff' : '#ff1493'}`
          }}
        >
          <h2 
            className="text-2xl md:text-3xl font-bold text-center mb-4"
            style={{
              color: isDark ? '#ff00ff' : '#ff1493',
              textShadow: isDark ? '0 0 15px rgba(255, 0, 255, 0.6)' : 'none'
            }}
          >
            {t('brandBoost.advertisingServices')}
          </h2>
          <p 
            className="text-center max-w-2xl mx-auto mb-10"
            style={{ color: isDark ? '#a0a0a0' : '#64748b' }}
          >
            {t('brandBoost.advertisingServicesSubtitle')}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {advertisingServices.map((item, index) => (
              <div 
                key={index}
                className="p-5 rounded-xl text-center"
                style={{
                  backgroundColor: isDark ? 'rgba(0, 0, 0, 0.4)' : 'rgba(255, 255, 255, 0.8)'
                }}
              >
                <item.icon className="w-10 h-10 mx-auto mb-3" style={{ color: isDark ? '#00ffff' : '#0ea5e9' }} />
                <h3 
                  className="font-bold mb-2"
                  style={{ color: isDark ? '#ffffff' : '#0f172a' }}
                >
                  {item.title}
                </h3>
                <p 
                  className="text-sm"
                  style={{ color: isDark ? '#a0a0a0' : '#64748b' }}
                >
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Brand Image Services */}
        <div className="mb-20">
          <h2 
            className="text-3xl md:text-4xl font-bold text-center mb-4"
            style={{
              color: isDark ? '#ffff00' : '#f59e0b',
              textShadow: isDark ? '0 0 20px rgba(255, 255, 0, 0.6)' : 'none'
            }}
          >
            {t('brandBoost.brandImageConsulting')}
          </h2>
          <p 
            className="text-center max-w-2xl mx-auto mb-12"
            style={{ color: isDark ? '#a0a0a0' : '#64748b' }}
          >
            {t('brandBoost.brandImageConsultingSubtitle')}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {brandImageServices.map((service, index) => (
              <div 
                key={index}
                className="p-6 rounded-xl"
                style={{
                  backgroundColor: isDark ? 'rgba(10, 10, 10, 0.8)' : 'rgba(248, 250, 252, 1)',
                  border: `1px solid ${isDark ? 'rgba(255, 255, 0, 0.3)' : 'rgba(245, 158, 11, 0.3)'}`
                }}
              >
                <h3 
                  className="font-bold mb-4 pb-3 border-b"
                  style={{ 
                    color: isDark ? '#ffff00' : '#f59e0b',
                    borderColor: isDark ? 'rgba(255, 255, 0, 0.2)' : 'rgba(245, 158, 11, 0.2)'
                  }}
                >
                  {service.title}
                </h3>
                <ul className="space-y-2">
                  {service.items.map((item, iIndex) => (
                    <li 
                      key={iIndex}
                      className="flex items-center gap-2 text-sm"
                      style={{ color: isDark ? '#a0a0a0' : '#64748b' }}
                    >
                      <Check className="w-4 h-4 flex-shrink-0" style={{ color: isDark ? '#00ff88' : '#10b981' }} />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Process Section */}
        <div className="mb-20">
          <h2 
            className="text-3xl md:text-4xl font-bold text-center mb-12"
            style={{
              color: isDark ? '#00ffff' : '#0ea5e9',
              textShadow: isDark ? '0 0 20px rgba(0, 255, 255, 0.6)' : 'none'
            }}
          >
            {t('brandBoost.howWeWork')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {processSteps.map((item, index) => (
              <div 
                key={index}
                className="p-6 rounded-xl text-center"
                style={{
                  backgroundColor: isDark ? 'rgba(10, 10, 10, 0.8)' : 'rgba(248, 250, 252, 1)'
                }}
              >
                <div 
                  className="text-5xl font-bold mb-4"
                  style={{
                    color: isDark ? '#00ffff' : '#0ea5e9',
                    textShadow: isDark ? '0 0 20px rgba(0, 255, 255, 0.6)' : 'none'
                  }}
                >
                  {item.step}
                </div>
                <h3 
                  className="text-lg font-bold mb-2"
                  style={{ color: isDark ? '#ffffff' : '#0f172a' }}
                >
                  {item.title}
                </h3>
                <p 
                  className="text-sm"
                  style={{ color: isDark ? '#a0a0a0' : '#64748b' }}
                >
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Who We Help */}
        <div 
          className="p-8 rounded-2xl mb-20"
          style={{
            backgroundColor: isDark ? 'rgba(10, 10, 10, 0.9)' : 'rgba(248, 250, 252, 1)',
            border: `2px solid ${isDark ? '#00ff88' : '#10b981'}`
          }}
        >
          <h2 
            className="text-2xl md:text-3xl font-bold text-center mb-8"
            style={{
              color: isDark ? '#00ff88' : '#10b981',
              textShadow: isDark ? '0 0 15px rgba(0, 255, 136, 0.6)' : 'none'
            }}
          >
            {t('brandBoost.businessesWeHelp')}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {whoWeHelp.map((business, index) => (
              <div 
                key={index}
                className="flex items-center gap-2 p-3 rounded-lg"
                style={{
                  backgroundColor: isDark ? 'rgba(0, 0, 0, 0.3)' : 'rgba(255, 255, 255, 0.8)'
                }}
              >
                <Check className="w-4 h-4 flex-shrink-0" style={{ color: isDark ? '#00ff88' : '#10b981' }} />
                <span 
                  className="text-sm"
                  style={{ color: isDark ? '#e0e0e0' : '#334155' }}
                >
                  {business}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Contact Form */}
        <div 
          className="max-w-2xl mx-auto p-8 rounded-2xl"
          style={{
            backgroundColor: isDark ? 'rgba(20, 20, 20, 0.8)' : 'rgba(255, 255, 255, 1)',
            border: `3px solid ${isDark ? '#00ffff' : '#00bfff'}`,
            boxShadow: isDark ? '0 0 50px rgba(0, 255, 255, 0.3)' : '0 0 40px rgba(0, 191, 255, 0.2)'
          }}
        >
          <h2 
            className="text-2xl font-bold mb-2 text-center"
            style={{
              color: isDark ? '#00ffff' : '#0ea5e9',
              textShadow: isDark ? '0 0 15px rgba(0, 255, 255, 0.6)' : 'none'
            }}
          >
            {t('brandBoost.scheduleConsultation')}
          </h2>
          <p 
            className="mb-6 text-center"
            style={{ color: isDark ? '#a0a0a0' : '#64748b' }}
          >
            {t('brandBoost.scheduleConsultationSubtitle')}
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: isDark ? '#e0e0e0' : '#334155' }}>
                  {t('brandBoost.yourName')} *
                </label>
                <Input
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder={t('quoteForm.namePlaceholder')}
                  style={{
                    backgroundColor: isDark ? 'rgba(0, 0, 0, 0.5)' : '#f8fafc',
                    borderColor: isDark ? '#00ffff' : '#e2e8f0',
                    color: isDark ? '#ffffff' : '#0f172a'
                  }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: isDark ? '#e0e0e0' : '#334155' }}>
                  {t('brandBoost.email')} *
                </label>
                <Input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  placeholder={t('quoteForm.emailPlaceholder')}
                  style={{
                    backgroundColor: isDark ? 'rgba(0, 0, 0, 0.5)' : '#f8fafc',
                    borderColor: isDark ? '#00ffff' : '#e2e8f0',
                    color: isDark ? '#ffffff' : '#0f172a'
                  }}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: isDark ? '#e0e0e0' : '#334155' }}>
                  {t('brandBoost.phone')}
                </label>
                <Input
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder={t('quoteForm.phonePlaceholder')}
                  style={{
                    backgroundColor: isDark ? 'rgba(0, 0, 0, 0.5)' : '#f8fafc',
                    borderColor: isDark ? '#00ffff' : '#e2e8f0',
                    color: isDark ? '#ffffff' : '#0f172a'
                  }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: isDark ? '#e0e0e0' : '#334155' }}>
                  {t('brandBoost.businessName')} *
                </label>
                <Input
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  required
                  placeholder={t('brandBoost.businessTypePlaceholder')}
                  style={{
                    backgroundColor: isDark ? 'rgba(0, 0, 0, 0.5)' : '#f8fafc',
                    borderColor: isDark ? '#00ffff' : '#e2e8f0',
                    color: isDark ? '#ffffff' : '#0f172a'
                  }}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: isDark ? '#e0e0e0' : '#334155' }}>
                {t('brandBoost.typeOfBusiness')}
              </label>
              <Input
                name="businessType"
                value={formData.businessType}
                onChange={handleInputChange}
                placeholder={t('brandBoost.businessTypePlaceholder')}
                style={{
                  backgroundColor: isDark ? 'rgba(0, 0, 0, 0.5)' : '#f8fafc',
                  borderColor: isDark ? '#00ffff' : '#e2e8f0',
                  color: isDark ? '#ffffff' : '#0f172a'
                }}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: isDark ? '#e0e0e0' : '#334155' }}>
                {t('brandBoost.challenges')} *
              </label>
              <Textarea
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                required
                rows={5}
                placeholder={t('brandBoost.challengesPlaceholder')}
                className="resize-none"
                style={{
                  backgroundColor: isDark ? 'rgba(0, 0, 0, 0.5)' : '#f8fafc',
                  borderColor: isDark ? '#00ffff' : '#e2e8f0',
                  color: isDark ? '#ffffff' : '#0f172a'
                }}
              />
            </div>

            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-6 text-lg font-bold transition-all duration-300 hover:scale-105"
              style={{
                background: isDark 
                  ? 'linear-gradient(135deg, #00ffff 0%, #00ff88 100%)'
                  : 'linear-gradient(135deg, #0ea5e9 0%, #10b981 100%)',
                color: isDark ? '#000000' : '#ffffff',
                boxShadow: isDark 
                  ? '0 10px 40px rgba(0, 255, 255, 0.5)' 
                  : '0 10px 30px rgba(14, 165, 233, 0.4)'
              }}
            >
              {isSubmitting ? t('brandBoost.sending') : (
                <>
                  <Send className="w-5 h-5 mr-2" />
                  {t('brandBoost.requestConsultation')}
                </>
              )}
            </Button>

            <p 
              className="text-center text-sm"
              style={{ color: isDark ? '#707070' : '#94a3b8' }}
            >
              {t('brandBoost.consultationDisclaimer')}
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BusinessEnhancement;
